import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { VerifiedBadge } from '@/components/VerifiedBadge';
import BoostModal from '@/components/BoostModal';
import ListingCard from '@/components/ListingCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { PasswordInput } from '@/components/PasswordInput';
import {
  Plus, Package, User, ShieldCheck, Pencil, Trash2, Rocket,
  Star, Search, Home, MapPin, Eye, CheckCircle, Clock, XCircle, Heart, Sun, Moon, Lock, MessageSquare,
} from 'lucide-react';

const TABS_MOBILE = [
  { key: 'listings', icon: Package },
  { key: 'verification', icon: ShieldCheck },
  { key: 'favorites', icon: Heart },
  { key: 'profile', icon: User },
];

const TABS_DESKTOP = [
  { key: 'listings', icon: Package },
  { key: 'favorites', icon: Heart },
  { key: 'verification', icon: ShieldCheck },
  { key: 'profile', icon: User },
];

export default function DashboardPage() {
  const { user, refreshUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, tc, lang } = useTranslation('dashboard');
  const locale = lang === 'nl' ? 'nl-NL' : 'fr-FR';
  const { t: tVerif } = useTranslation('verification');
  const { t: tCond } = useTranslation('conditions');
  const { t: ta } = useTranslation('account');

  const activeTab = searchParams.get('tab') || 'listings';
  const setActiveTab = (tab) => setSearchParams({ tab });

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boostListing, setBoostListing] = useState(null);

  // Favorites
  const [favorites, setFavorites] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);

  // Password change
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new_password: '', confirm: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Delete account
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Delete listing
  const [showDeleteListing, setShowDeleteListing] = useState(null);
  const [deleteListingReason, setDeleteListingReason] = useState('');
  const [deleteListingDetail, setDeleteListingDetail] = useState('');
  const [deleteListingLoading, setDeleteListingLoading] = useState(false);

  // Profile
  const [profileForm, setProfileForm] = useState({
    first_name: '', last_name: '', company_name: '', phone: '', company_email: '',
  });
  const [profileLoading, setProfileLoading] = useState(false);

  // Verification
  const [verifForm, setVerifForm] = useState({
    company_name: '', vat_number: '', company_address: '', country: 'Belgique', comment: '',
  });
  const [verifLoading, setVerifLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setProfileForm({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      company_name: user.company?.name || '',
      phone: user.company?.phone || '',
      company_email: user.company?.email || user.email || '',
    });
    if (user.verification_data) {
      setVerifForm(prev => ({ ...prev, ...user.verification_data }));
    }
  }, [user]);

  useEffect(() => {
    api.get('/listings/my')
      .then(r => setListings(r.data.listings || r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Load favorites when tab is selected
  useEffect(() => {
    if (activeTab === 'favorites') {
      setFavoritesLoading(true);
      api.get('/favorites')
        .then(r => {
          setFavorites(r.data.listings || []);
          setFavoriteIds(r.data.listing_ids || []);
        })
        .catch(() => {})
        .finally(() => setFavoritesLoading(false));
    }
  }, [activeTab]);

  const toggleFavorite = useCallback(async (listingId) => {
    try {
      const { data } = await api.post(`/favorites/${listingId}`);
      if (!data.favorited) {
        setFavorites(prev => prev.filter(l => l.id !== listingId));
        setFavoriteIds(prev => prev.filter(id => id !== listingId));
      }
    } catch {}
  }, []);

  const handleChangePassword = async () => {
    if (!passwordForm.current || !passwordForm.new_password) { toast.error(ta('fillAll')); return; }
    if (passwordForm.new_password.length < 6) { toast.error(ta('minLength')); return; }
    if (passwordForm.new_password !== passwordForm.confirm) { toast.error(ta('mismatch')); return; }
    setPasswordLoading(true);
    try {
      await api.put('/auth/change-password', { current_password: passwordForm.current, new_password: passwordForm.new_password });
      toast.success(ta('changed'));
      setPasswordForm({ current: '', new_password: '', confirm: '' });
      setShowPasswordForm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || ta('changeError'));
    } finally { setPasswordLoading(false); }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) { toast.error(ta('deletePassword')); return; }
    if (!deleteReason.trim()) { toast.error(ta('deleteReasonRequired')); return; }
    setDeleteLoading(true);
    try {
      await api.delete('/auth/delete-account', { data: { password: deletePassword, reason: deleteReason.trim() } });
      toast.success(ta('deleted'));
      window.location.href = '/';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression.');
    } finally { setDeleteLoading(false); }
  };

  const handleDelete = async () => {
    if (!showDeleteListing || !deleteListingReason) return;
    setDeleteListingLoading(true);
    try {
      await api.delete(`/listings/${showDeleteListing.id}?reason=${deleteListingReason}&reason_detail=${encodeURIComponent(deleteListingDetail)}`);
      setListings(prev => prev.filter(l => l.id !== showDeleteListing.id));
      toast.success(t('deleted'));
      setShowDeleteListing(null);
      setDeleteListingReason('');
      setDeleteListingDetail('');
    } catch {
      toast.error(t('deleteError'));
    } finally { setDeleteListingLoading(false); }
  };

  const handleProfileSave = async () => {
    setProfileLoading(true);
    try {
      await api.put('/auth/profile', {
        first_name: profileForm.first_name,
        last_name: profileForm.last_name,
        company: {
          ...(user.company || {}),
          name: profileForm.company_name,
          phone: profileForm.phone,
          email: profileForm.company_email,
        }
      });
      await refreshUser();
      toast.success(t('profileUpdated'));
    } catch {
      toast.error(t('profileError'));
    } finally {
      setProfileLoading(false);
    }
  };

  const handleVerifSubmit = async () => {
    if (!verifForm.company_name || !verifForm.vat_number || !verifForm.company_address) {
      toast.error(tVerif('fillRequired'));
      return;
    }
    setVerifLoading(true);
    try {
      await api.post('/auth/request-verification', verifForm);
      await refreshUser();
      toast.success(tVerif('submitted'));
    } catch {
      toast.error(tVerif('errorSubmit'));
    } finally {
      setVerifLoading(false);
    }
  };

  const tabLabels = { listings: t('myListings'), favorites: t('favorites'), profile: t('profile'), verification: t('verification') };

  if (!user) {
    navigate('/connexion');
    return null;
  }

  return (
    <div className="site-container py-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold text-neutral-900">{t('title')}</h1>
          <p className="text-sm text-neutral-500">{t('welcome')} {user.first_name}</p>
          <div className="flex items-center gap-2 mt-1">
            {user.verification_status === 'verified' && <VerifiedBadge size="xs" />}
            {user.role === 'admin' && (
              <span className="text-[10px] font-semibold text-[#1F3F72] bg-blue-50 px-2 py-0.5 rounded-full">
                {t('administrator')}
              </span>
            )}
          </div>
        </div>
        <Button className="bg-[#F25C05] hover:bg-[#d94f04] text-white" onClick={() => navigate('/publier')} data-testid="dashboard-publish-btn">
          <Plus className="w-4 h-4 mr-1" /> {t('publishAd')}
        </Button>
      </div>

      {/* Tabs - Mobile (2x2 grid) */}
      <div className="grid grid-cols-2 gap-2 mb-6 sm:hidden" data-testid="dashboard-tabs-mobile">
        {TABS_MOBILE.map((tab) => {
          const isVerif = tab.key === 'verification';
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors rounded-lg whitespace-nowrap ${
                isActive
                  ? 'bg-[#1F3F72] text-white'
                  : isVerif && user?.verification_status !== 'verified'
                    ? 'bg-[#F25C05]/10 text-[#F25C05] border border-[#F25C05]/30'
                    : 'bg-neutral-100 text-neutral-500 hover:text-neutral-700'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <tab.icon className="w-4 h-4" /> {tabLabels[tab.key]}
              {isVerif && user?.verification_status !== 'verified' && (
                <span className="w-1.5 h-1.5 rounded-full bg-[#F25C05]" />
              )}
            </button>
          );
        })}
      </div>
      {/* Tabs - Desktop (horizontal) */}
      <div className="hidden sm:flex gap-1 mb-6 border-b" data-testid="dashboard-tabs">
        {TABS_DESKTOP.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
                isActive
                  ? 'border-[#1F3F72] text-[#1F3F72]'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700'
              }`}
              data-testid={`tab-desktop-${tab.key}`}
            >
              <tab.icon className="w-4 h-4" /> {tabLabels[tab.key]}
            </button>
          );
        })}
      </div>

      {/* Listings Tab */}
      {activeTab === 'listings' && (
        <div data-testid="tab-listings-content">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3F72] mx-auto" />
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-16" data-testid="no-listings">
              <Package className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
              <p className="text-neutral-500 mb-4">{t('noListings')}</p>
              <Button className="bg-[#F25C05] text-white" onClick={() => navigate('/publier')}>
                <Plus className="w-4 h-4 mr-1" /> {t('firstListing')}
              </Button>
            </div>
          ) : (
            <div className="space-y-5">
              {listings.map((listing) => (
                <div key={listing.id} className="bg-white border rounded-xl p-5 flex flex-col sm:flex-row gap-5" data-testid={`dashboard-listing-${listing.id}`}>
                  {/* Image */}
                  <Link to={`/annonces/${listing.id}`} className="w-full sm:w-52 h-40 rounded-lg overflow-hidden bg-neutral-100 shrink-0 block">
                    <img
                      src={listing.images?.[0] ? (listing.images[0].startsWith('http') ? listing.images[0] : `${process.env.REACT_APP_BACKEND_URL}${listing.images[0]}`) : 'https://images.unsplash.com/photo-1631856956334-35db20cb7748?w=200&h=150&fit=crop'}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </Link>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <Link to={`/annonces/${listing.id}`} className="font-heading text-lg font-bold text-neutral-900 hover:text-[#1F3F72] line-clamp-2">
                          {listing.title}
                        </Link>
                        <p className="text-xl font-bold text-[#F25C05] mt-1">
                          {listing.price?.toLocaleString(locale, { style: 'currency', currency: 'EUR' })}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate(`/annonces/${listing.id}/modifier`)} data-testid={`edit-${listing.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-red-500 hover:text-red-600" onClick={() => setShowDeleteListing(listing)} data-testid={`delete-${listing.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-3 text-sm text-neutral-500">
                      {listing.condition && (
                        <span className="bg-neutral-100 px-2.5 py-1 rounded-md text-xs font-medium text-neutral-700">{tCond(listing.condition)}</span>
                      )}
                      {listing.location && (
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{listing.location}</span>
                      )}
                      {listing.views !== undefined && (
                        <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{listing.views}</span>
                      )}
                    </div>
                    {/* Boosts */}
                    {(listing.active_boosts?.search_top || listing.active_boosts?.homepage || listing.active_boosts?.badge) && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {listing.active_boosts?.search_top && (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-md">
                            <Search className="w-3.5 h-3.5" /> {t('boosted')}
                          </span>
                        )}
                        {listing.active_boosts?.homepage && (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-purple-600 bg-purple-50 px-3 py-1 rounded-md">
                            <Home className="w-3.5 h-3.5" /> {t('homepage')}
                          </span>
                        )}
                        {listing.active_boosts?.badge && (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#F25C05] bg-orange-50 px-3 py-1 rounded-md">
                            <Star className="w-3.5 h-3.5 fill-current" /> Top
                          </span>
                        )}
                      </div>
                    )}
                    {/* Boost button */}
                    <Button
                      size="sm"
                      className="mt-3 bg-[#F25C05] hover:bg-[#d94f04] text-white"
                      onClick={() => setBoostListing(listing)}
                      data-testid={`boost-btn-${listing.id}`}
                    >
                      <Rocket className="w-4 h-4 mr-1.5" /> Boost
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Favorites Tab */}
      {activeTab === 'favorites' && (
        <div data-testid="tab-favorites-content">
          {favoritesLoading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F3F72] mx-auto" />
            </div>
          ) : favorites.length === 0 ? (
            <div className="text-center py-16" data-testid="no-favorites">
              <Heart className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
              <p className="text-neutral-500 mb-4">{t('noFavorites')}</p>
              <Button className="bg-[#F25C05] text-white" onClick={() => navigate('/annonces')}>
                {t('browseListing')}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-4 gap-5">
              {favorites.map((listing) => (
                <ListingCard
                  key={listing.id}
                  listing={listing}
                  isFavorited={favoriteIds.includes(listing.id)}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>
          )}
        </div>
      )}


      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <div className="max-w-xl space-y-5" data-testid="tab-profile-content">
          {/* Personal info */}
          <div className="bg-white border rounded-xl p-6 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('firstName')}</Label>
                <Input value={profileForm.first_name} onChange={e => setProfileForm(p => ({ ...p, first_name: e.target.value }))} data-testid="profile-firstname" />
              </div>
              <div className="space-y-2">
                <Label>{t('lastName')}</Label>
                <Input value={profileForm.last_name} onChange={e => setProfileForm(p => ({ ...p, last_name: e.target.value }))} data-testid="profile-lastname" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('companyName')}</Label>
              <Input value={profileForm.company_name} onChange={e => setProfileForm(p => ({ ...p, company_name: e.target.value }))} data-testid="profile-company" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('phone')}</Label>
                <Input value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} data-testid="profile-phone" />
              </div>
              <div className="space-y-2">
                <Label>{t('companyEmail')}</Label>
                <Input value={profileForm.company_email} onChange={e => setProfileForm(p => ({ ...p, company_email: e.target.value }))} data-testid="profile-company-email" />
              </div>
            </div>
            <Button onClick={handleProfileSave} className="bg-[#1F3F72] text-white" disabled={profileLoading} data-testid="profile-save-btn">
              {profileLoading ? t('saving') : t('saveChanges')}
            </Button>
          </div>

          {/* Preferences row: theme + password */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Theme toggle - discrete */}
            <div className="bg-white border rounded-xl p-4 flex items-center justify-between" data-testid="theme-selector">
              <div className="flex items-center gap-2.5">
                {theme === 'dark' ? <Moon className="w-4 h-4 text-blue-400" /> : <Sun className="w-4 h-4 text-amber-500" />}
                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{ta('appearance')}</span>
              </div>
              <button
                onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                className={`relative w-11 h-6 rounded-full transition-colors ${theme === 'dark' ? 'bg-[#1F3F72]' : 'bg-neutral-200'}`}
                data-testid="theme-toggle-profile"
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${theme === 'dark' ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {/* Change password */}
            <div className="bg-white border rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Lock className="w-4 h-4 text-neutral-500" />
                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{ta('password')}</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowPasswordForm(!showPasswordForm)} data-testid="change-password-toggle">
                {ta('changeBtn')}
              </Button>
            </div>
          </div>

          {/* Password change form */}
          {showPasswordForm && (
            <div className="bg-white border rounded-xl p-6 space-y-4" data-testid="password-change-form">
              <div className="space-y-2">
                <Label>{ta('currentPassword')}</Label>
                <PasswordInput value={passwordForm.current} onChange={e => setPasswordForm(p => ({ ...p, current: e.target.value }))} data-testid="current-password-input" />
              </div>
              <div className="space-y-2">
                <Label>{ta('newPassword')}</Label>
                <PasswordInput value={passwordForm.new_password} onChange={e => setPasswordForm(p => ({ ...p, new_password: e.target.value }))} placeholder={ta('newPlaceholder')} data-testid="new-password-input" />
              </div>
              <div className="space-y-2">
                <Label>{ta('confirm')}</Label>
                <PasswordInput value={passwordForm.confirm} onChange={e => setPasswordForm(p => ({ ...p, confirm: e.target.value }))} data-testid="confirm-password-input" />
              </div>
              <Button onClick={handleChangePassword} className="bg-[#1F3F72] text-white" disabled={passwordLoading} data-testid="change-password-btn">
                {passwordLoading ? ta('changing') : ta('changePassword')}
              </Button>
            </div>
          )}

          {/* Delete account - discrete */}
          <div className="pt-4 border-t mt-2" data-testid="delete-account-section">
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="text-xs text-neutral-400 hover:text-red-500 transition-colors"
                data-testid="delete-account-toggle"
              >
                {ta('deleteAccount')}
              </button>
            ) : (
              <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-4 space-y-3" data-testid="delete-confirm-form">
                <p className="text-xs text-red-500">{ta('deleteWarning')}</p>
                <div className="space-y-1">
                  <Label className="text-xs text-neutral-600 dark:text-neutral-400">{ta('deleteReasonLabel')}</Label>
                  <textarea
                    value={deleteReason}
                    onChange={e => setDeleteReason(e.target.value)}
                    placeholder={ta('deleteReasonPlaceholder')}
                    className="w-full text-sm rounded-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 px-3 py-2 min-h-[70px] resize-none focus:outline-none focus:ring-2 focus:ring-[#1F3F72] dark:text-white"
                    data-testid="delete-reason-input"
                  />
                </div>
                <PasswordInput value={deletePassword} onChange={e => setDeletePassword(e.target.value)} placeholder={ta('deletePasswordPlaceholder')} className="text-sm" data-testid="delete-password-input" />
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={handleDeleteAccount} className="bg-red-500 hover:bg-red-600 text-white text-xs" disabled={deleteLoading || !deleteReason.trim()} data-testid="delete-confirm-btn">
                    {deleteLoading ? ta('deleting') : ta('deleteConfirm')}
                  </Button>
                  <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); setDeleteReason(''); }}>
                    {tc('cancel')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Verification Tab */}
      {activeTab === 'verification' && (
        <div className="max-w-xl" data-testid="tab-verification-content">
          {user.verification_status === 'verified' ? (
            <div className="bg-white border rounded-xl p-8 text-center" data-testid="verification-verified">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 mb-4">
                <CheckCircle className="w-7 h-7 text-emerald-600" />
              </div>
              <h2 className="font-heading text-xl font-bold text-neutral-900 mb-2">{tVerif('verifiedTitle')}</h2>
              <p className="text-sm text-neutral-600 mb-4">{tVerif('verifiedDesc')}</p>
              {user.verification_data && (
                <div className="bg-neutral-50 rounded-lg p-4 text-left text-sm space-y-1 mt-4">
                  <p className="font-semibold text-xs text-neutral-400 mb-2">{tVerif('verifiedInfo')}</p>
                  <p><span className="text-neutral-500">{tVerif('companyName')}:</span> {user.verification_data.company_name}</p>
                  <p><span className="text-neutral-500">{tVerif('vatNumber')}:</span> {user.verification_data.vat_number}</p>
                  <p><span className="text-neutral-500">{tVerif('companyAddress')}:</span> {user.verification_data.company_address}</p>
                </div>
              )}
            </div>
          ) : user.verification_status === 'pending' ? (
            <div className="bg-white border rounded-xl p-8 text-center" data-testid="verification-pending">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 mb-4">
                <Clock className="w-7 h-7 text-amber-600" />
              </div>
              <h2 className="font-heading text-xl font-bold text-neutral-900 mb-2">{tVerif('pendingTitle')}</h2>
              <p className="text-sm text-neutral-600">{tVerif('pendingDesc')}</p>
              {user.verification_data && (
                <div className="bg-neutral-50 rounded-lg p-4 text-left text-sm space-y-1 mt-4">
                  <p className="font-semibold text-xs text-neutral-400 mb-2">{tVerif('submittedInfo')}</p>
                  <p><span className="text-neutral-500">{tVerif('companyName')}:</span> {user.verification_data.company_name}</p>
                  <p><span className="text-neutral-500">{tVerif('vatNumber')}:</span> {user.verification_data.vat_number}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white border rounded-xl p-6 space-y-5" data-testid="verification-form">
              {user.verification_status === 'rejected' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-1">
                    <XCircle className="w-4 h-4 text-red-500" />
                    <h3 className="font-heading text-sm font-semibold text-red-800">{tVerif('rejectedTitle')}</h3>
                  </div>
                  <p className="text-xs text-red-600">{tVerif('rejectedDesc')}</p>
                </div>
              )}
              <div>
                <h2 className="font-heading text-lg font-semibold text-neutral-900">{tVerif('verifyTitle')}</h2>
                <p className="text-sm text-neutral-500 mt-1">{tVerif('verifyDesc')}</p>
              </div>
              <div className="space-y-2">
                <Label>{tVerif('companyName')} *</Label>
                <Input value={verifForm.company_name} onChange={e => setVerifForm(p => ({ ...p, company_name: e.target.value }))} data-testid="verif-company-name" />
              </div>
              <div className="space-y-2">
                <Label>{tVerif('vatNumber')} *</Label>
                <Input placeholder="BE 0XXX.XXX.XXX" value={verifForm.vat_number} onChange={e => setVerifForm(p => ({ ...p, vat_number: e.target.value }))} data-testid="verif-vat" />
              </div>
              <div className="space-y-2">
                <Label>{tVerif('companyAddress')} *</Label>
                <Textarea rows={2} value={verifForm.company_address} onChange={e => setVerifForm(p => ({ ...p, company_address: e.target.value }))} data-testid="verif-address" />
              </div>
              <div className="space-y-2">
                <Label>{tVerif('country')}</Label>
                <Input value={verifForm.country} onChange={e => setVerifForm(p => ({ ...p, country: e.target.value }))} data-testid="verif-country" />
              </div>
              <div className="space-y-2">
                <Label>{tVerif('comment')}</Label>
                <Textarea rows={2} value={verifForm.comment} onChange={e => setVerifForm(p => ({ ...p, comment: e.target.value }))} data-testid="verif-comment" />
              </div>
              <Button onClick={handleVerifSubmit} className="bg-[#1F3F72] text-white" disabled={verifLoading} data-testid="verif-submit-btn">
                {verifLoading ? tVerif('submitting') : <><ShieldCheck className="w-4 h-4 mr-2" /> {tVerif('submitBtn')}</>}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Boost Modal */}
      {boostListing && <BoostModal listing={boostListing} onClose={() => setBoostListing(null)} />}

      {/* Delete Listing Modal */}
      {showDeleteListing && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50" data-testid="delete-listing-modal" onClick={() => { setShowDeleteListing(null); setDeleteListingReason(''); setDeleteListingDetail(''); }}>
          <div className="bg-white rounded-t-2xl p-6 pb-8 w-full max-w-3xl space-y-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-bold text-neutral-900">{t('deleteTitle')}</h3>

            <div className="space-y-2">
              {[
                { value: 'sold_destockpro', label: t('deleteReasonSold'), icon: '', color: 'border-emerald-300 bg-emerald-50 text-emerald-800' },
                { value: 'sold_elsewhere', label: t('deleteReasonElsewhere'), icon: '', color: 'border-blue-200 bg-blue-50 text-blue-800' },
                { value: 'not_available', label: t('deleteReasonUnavailable'), icon: '', color: 'border-neutral-200 bg-neutral-50 text-neutral-700' },
                { value: 'other', label: t('deleteReasonOther'), icon: '', color: 'border-neutral-200 bg-neutral-50 text-neutral-700' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDeleteListingReason(opt.value)}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    deleteListingReason === opt.value
                      ? `${opt.color} ring-2 ring-offset-1 ${opt.value === 'sold_destockpro' ? 'ring-emerald-400' : 'ring-[#1F3F72]/30'}`
                      : 'border-neutral-200 hover:border-neutral-300 text-neutral-600'
                  }`}
                  data-testid={`delete-reason-${opt.value}`}
                >
                  {opt.icon && <span className="mr-2">{opt.icon}</span>}
                  {opt.label}
                </button>
              ))}
            </div>

            {deleteListingReason === 'other' && (
              <Textarea
                placeholder={t('deleteReasonDetailPlaceholder')}
                value={deleteListingDetail}
                onChange={(e) => setDeleteListingDetail(e.target.value)}
                rows={2}
                className="text-sm"
                data-testid="delete-reason-detail"
              />
            )}

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => { setShowDeleteListing(null); setDeleteListingReason(''); setDeleteListingDetail(''); }}
                data-testid="delete-listing-cancel"
              >
                {t('deleteCancel')}
              </Button>
              <Button
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                onClick={handleDelete}
                disabled={!deleteListingReason || deleteListingLoading}
                data-testid="delete-listing-confirm"
              >
                {deleteListingLoading ? t('deleteDeleting') : t('deleteConfirmBtn')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
